import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, ChevronRight, Loader2, MapPin, ScanLine } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { ScanTextInput } from '../../../common/inputs';
import { AuthService } from '../../../../services/authService';
import { stcBinService } from '../../../../services/stcBinService';
import { StcBinDto } from '../../../../types/stcBin';
import { fetchLocationOverview } from './useLocationOverview';

interface LocationLookupSearchProps {
  warehouseId: string | null;
  onSelect: (binCode: string) => void;
  onBack?: () => void;
}

// Delsøket bruker stc-bins/search (TOP 50, LIKE på lokasjonskode/beskrivelse) — 3 tegn holder det raskt.
const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;
const OPERATOR_LOCATION_CATEGORY = 5; // StcBinCatNo — BinCategoryCodes.OperatorLocation

/**
 * Endepunktet treffer også på sone og lager-ID. Behold bare lokasjoner der koden eller beskrivelsen
 * inneholder søketeksten, og aldri operatørlokasjoner (kategori 5).
 */
const filterMatches = (bins: StcBinDto[], value: string): StcBinDto[] => {
  const needle = value.toLowerCase();
  return bins.filter(b =>
    Number(b.stcBinCatNo) !== OPERATOR_LOCATION_CATEGORY
    && ((b.binCode || '').toLowerCase().includes(needle) || (b.description1 || '').toLowerCase().includes(needle)));
};

/**
 * Skann eller skriv en lokasjon. Skanning skjer kun i dette fokuserte feltet (ingen global scanner-hook),
 * som i varekortet. Fra 3 tegn vises lokasjoner i aktivt lager som inneholder teksten.
 * Enter: eksakt lokasjon åpnes direkte; ellers åpnes et enkelt treff, eller listen blir stående.
 * Finnes ingenting, blir operatøren stående her med feltet tømt, klar for neste skann.
 */
export const LocationLookupSearch: React.FC<LocationLookupSearchProps> = ({ warehouseId, onSelect, onBack }) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState<StcBinDto[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [notFoundTerm, setNotFoundTerm] = useState<string | null>(null);
  const [operatorBin, setOperatorBin] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchIdRef = useRef(0);

  const runSearch = useCallback(async (value: string): Promise<StcBinDto[]> => {
    if (!warehouseId) return [];
    const id = ++searchIdRef.current;
    setLoading(true);
    try {
      const companyNo = AuthService.getUserDataFromToken()?.companyNo ?? '';
      const result = filterMatches(await stcBinService.search(companyNo, value, warehouseId), value);
      if (id === searchIdRef.current) {
        setMatches(result);
        setHasSearched(true);
      }
      return result;
    } catch (err) {
      if (id === searchIdRef.current) {
        console.error('Error searching locations:', err);
        setError(t('warehouse.modules.locationLookup.search.error', {}, 'Kunne ikke slå opp lokasjonen'));
      }
      return [];
    } finally {
      if (id === searchIdRef.current) setLoading(false);
    }
  }, [warehouseId, t]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const value = term.trim();
    if (value.length < MIN_SEARCH_LENGTH) {
      searchIdRef.current++;
      setMatches([]);
      setHasSearched(false);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => { void runSearch(value); }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [term, runSearch]);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const value = term.trim();
    if (!value || !warehouseId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    setLoading(true);
    setNotFoundTerm(null);
    setOperatorBin(null);
    setError(null);
    try {
      // Eksakt lokasjon først — skann går rett til lokasjonen
      const exact = await fetchLocationOverview(warehouseId, value);
      if (exact?.isOperatorLocation) {
        setOperatorBin(exact.bin.binCode);
        setTerm('');
        return;
      }
      if (exact) {
        onSelect(exact.bin.binCode);
        return;
      }

      const result = await runSearch(value);
      if (result.length === 1) {
        onSelect(result[0].binCode);
      } else if (result.length === 0) {
        setNotFoundTerm(value);
        setTerm('');
      }
      // Flere treff: listen blir stående og operatøren velger
    } catch (err) {
      console.error('Error looking up location:', err);
      setError(t('warehouse.modules.locationLookup.search.error', {}, 'Kunne ikke slå opp lokasjonen'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          {onBack && <BackButton onClick={onBack} />}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {t('warehouse.modules.locationLookup.title', {}, 'Lokasjonsoppslag')}
              </h1>
            </div>
            <p className="text-sm text-gray-600">
              {t('warehouse.modules.locationLookup.description', {}, 'Skann eller skriv en lokasjon for å se hva som ligger der')}
            </p>
          </div>
        </div>

        <div className="relative">
          <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <ScanTextInput
            value={term}
            onChange={(e) => { setTerm(e.target.value); setNotFoundTerm(null); setOperatorBin(null); setError(null); }}
            onKeyDown={handleKeyDown}
            placeholder={t('warehouse.modules.locationLookup.search.placeholder', {}, 'Skann eller skriv lokasjon...')}
            autoFocus
            disabled={!warehouseId}
            className="w-full pl-10 pr-10 py-3 min-h-[44px] text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-blue-500" />}
        </div>

        {!warehouseId && (
          <p className="mt-3 text-sm text-orange-600">
            {t('warehouse.modules.locationLookup.search.noWarehouse', {}, 'Velg lager i toppfeltet før du slår opp en lokasjon')}
          </p>
        )}
        {notFoundTerm && warehouseId && (
          <p className="mt-3 text-sm text-orange-600">
            {t('warehouse.modules.locationLookup.search.notFound', { '0': notFoundTerm, '1': warehouseId },
              `Fant ikke lokasjon «${notFoundTerm}» i lager ${warehouseId}`)}
          </p>
        )}
        {operatorBin && (
          <p className="mt-3 text-sm text-orange-600">
            {t('warehouse.modules.locationLookup.search.operatorLocation', { '0': operatorBin },
              `«${operatorBin}» er en operatørlokasjon og vises ikke i lokasjonsoppslaget`)}
          </p>
        )}
        {error && (
          <div className="mt-3 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {hasSearched && !notFoundTerm && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {matches.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-10">
              {t('warehouse.modules.locationLookup.search.noResults', {}, 'Ingen lokasjoner funnet')}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {matches.map(bin => (
                <li key={`${bin.warehouseID}-${bin.binCode}`}>
                  <button
                    type="button"
                    onClick={() => onSelect(bin.binCode)}
                    className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] text-left hover:bg-gray-50 active:bg-gray-100"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 font-mono break-words">{bin.binCode}</div>
                      {bin.description1 && bin.description1 !== bin.binCode && (
                        <div className="text-sm text-gray-600 break-words">{bin.description1}</div>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
